import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, ClockIcon, AlertCircleIcon, CheckCircle2Icon } from 'lucide-react';
import { useRaastTheme, slideVariants, PAY_FROM_ACCOUNTS } from './RaastShared';
import { TransactionReceipt } from '../TransactionReceipt';
interface RaastRequestsProps {
  onBack: () => void;
  onHome: () => void;
}
type Tab = 'Pending' | 'Expired' | 'Declined';
type Step = 'list' | 'detail' | 'otp' | 'success';
interface RequestItem {
  id: string;
  merchant: string;
  amount: number;
  type: 'now' | 'later';
  status: 'pending' | 'expired' | 'declined';
  expiryDate: string;
  iban: string;
  info?: string;
  timeLeft?: number; // seconds for 'now' type
}
const MOCK_REQUESTS: RequestItem[] = [{
  id: '1',
  merchant: 'K-Electric',
  amount: 4500,
  type: 'now',
  status: 'pending',
  expiryDate: 'Today, 11:59 PM',
  iban: 'PK36BOPU0000001234567890',
  timeLeft: 300,
  info: 'Monthly Bill'
}, {
  id: '2',
  merchant: 'Daraz.pk',
  amount: 12500,
  type: 'later',
  status: 'pending',
  expiryDate: '15 Aug 2026',
  iban: 'PK36BOPU0000000987654321',
  info: 'Order #12345'
}, {
  id: '3',
  merchant: 'Foodpanda',
  amount: 1200,
  type: 'expired',
  status: 'expired',
  expiryDate: '10 Jun 2026',
  iban: 'PK36BOPU0000001122334455'
}, {
  id: '4',
  merchant: 'Uber',
  amount: 850,
  type: 'declined',
  status: 'declined',
  expiryDate: '12 Jun 2026',
  iban: 'PK36BOPU0000005544332211'
}];
export function RaastRequests({
  onBack,
  onHome
}: RaastRequestsProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('list');
  const [direction, setDirection] = useState(1);
  const [activeTab, setActiveTab] = useState<Tab>('Pending');
  const [selectedRequest, setSelectedRequest] = useState<RequestItem | null>(null);
  const [requests, setRequests] = useState<RequestItem[]>(MOCK_REQUESTS);
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  // Countdown timer for 'now' requests
  useEffect(() => {
    const interval = setInterval(() => {
      setRequests(prev => prev.map(req => {
        if (req.type === 'now' && req.status === 'pending' && req.timeLeft && req.timeLeft > 0) {
          return {
            ...req,
            timeLeft: req.timeLeft - 1
          };
        }
        if (req.type === 'now' && req.status === 'pending' && req.timeLeft === 0) {
          return {
            ...req,
            status: 'expired'
          };
        }
        return req;
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`req-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`req-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  // Canonical header — back+label (16px regular) on left, icon-only maroon home on right
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2776" data-mp="1zzkxfpizmub4">
      <button onClick={onBackClick} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${textPri}`} aria-label="Back" data-id="element-2777" data-mp="1zxi0smnby2xf">
        <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2778" data-mp="28zhk5llthj3j" />
        <span className={`text-[16px] font-normal ${textPri}`} data-id="element-2779" data-mp="2dr9em783g8yy">{title}</span>
      </button>
      <div className="flex-1" data-id="element-2780" data-mp="2iqci3n8fe74t" />
      <button onClick={onHome} className={`p-2 -mr-2 ${isDark || isPlatinum ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-2781" data-mp="2iqlwseo0srv7">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2782" data-mp="2rr0l7mwa6n2x" />
      </button>
    </div>;
  // List header now shares the canonical style.
  const renderListHeader = renderHeader;
  const renderList = () => {
    const filteredRequests = requests.filter(r => r.status.toLowerCase() === activeTab.toLowerCase());
    // Sort so 'now' requests are at the top
    filteredRequests.sort((a, b) => a.type === 'now' ? -1 : 1);
    return <motion.div key="list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2783" data-mp="24xotyhugxtq">
        {renderListHeader('Payment Request', onBack)}
        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2784" data-mp="q0tdenh6wqiu">
          {/* Tabs — segmented control (image 2) */}
          <div className={`flex p-1 rounded-lg mb-6 ${isDark || isPlatinum ? 'bg-[#3A3A3C]' : 'bg-[#EFEDEB]'}`} data-id="element-2785" data-mp="l1yxpueiri2y">
            {(['Pending', 'Expired', 'Declined'] as Tab[]).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2.5 rounded-lg text-[13px] font-medium transition-all ${activeTab === tab ? isDark || isPlatinum ? 'bg-[#2A2A2C] text-[#F37024]' : 'bg-white text-[#F37024] shadow-sm' : textSec}`} data-id="element-2786" data-mp="1dpunsg9l2bqr">
                {tab}
              </button>)}
          </div>

          {/* List */}
          <div className="space-y-4" data-id="element-2787" data-mp="2obaarwma2dhc">
            {filteredRequests.length === 0 ? <div className="text-center py-12" data-id="element-2788" data-mp="2odn2v2lr2484">
                <AlertCircleIcon className={`w-12 h-12 mx-auto mb-3 ${textSec}`} data-id="element-2789" data-mp="2tbg6pozkym58" />
                <p className={`text-[14px] font-medium ${textPri}`} data-id="element-2790" data-mp="2fc6s2tr0w03d">
                  No {activeTab.toLowerCase()} requests
                </p>
              </div> : filteredRequests.map(req => <button key={req.id} onClick={() => {
            if (req.status === 'pending') {
              setSelectedRequest(req);
              navigateTo('detail');
            }
          }} className={`w-full text-left p-4 rounded-lg border shadow-sm ${cardBg} ${borderClr} active:scale-[0.99] transition-transform`} data-id="element-2791" data-mp="dhek608no9m9">
                  <div className="flex justify-between items-start mb-2" data-id="element-2792" data-mp="wnm27ohgvff6">
                    <h3 className={`text-[14px] font-medium ${textPri}`} data-id="element-2793" data-mp="1bcryb3l0cpiu">
                      {req.merchant}
                    </h3>
                    <span className={`text-[14px] font-medium ${textPri}`} data-id="element-2794" data-mp="16cm4phv6gsy4">
                      Rs. {req.amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-2795" data-mp="1gbbppjidetil">
                    <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2796" data-mp="1ga0g3328urjk">
                      {req.type === 'now' && req.status === 'pending' ? 'Expires in:' : 'Validity:'}
                    </span>
                    {req.type === 'now' && req.status === 'pending' ? <span className="text-[13px] font-medium text-[#EF4444] flex items-center gap-1" data-id="element-2797" data-mp="fhbotlwx2q8z">
                        <ClockIcon className="w-4 h-4" data-id="element-2798" data-mp="k8bmitdv1ccj" />
                        {formatTime(req.timeLeft || 0)}
                      </span> : <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2799" data-mp="3npab9gs8xoxc">
                        {req.expiryDate}
                      </span>}
                  </div>
                </button>)}
          </div>
        </div>
      </motion.div>;
  };
  const renderDetail = () => {
    if (!selectedRequest) return null;
    return <motion.div key="detail" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2800" data-mp="5hkubk8cwb8w">
        {renderHeader('Request Details', () => navigateTo('list', -1))}
        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2801" data-mp="1m3of8ndegy3m">
          <div className={`rounded-lg p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2802" data-mp="1m4zlctubihxw">
            <div className="flex flex-col items-center mb-8 text-center" data-id="element-2803" data-mp="1hb54rza0ej31">
              <p className={`text-[28px] font-bold ${textPri}`} data-id="element-2804" data-mp="1dal4imu52zco">
                Rs. {selectedRequest.amount.toLocaleString()}
              </p>
              <p className={`text-[14px] mt-1 ${textSec}`} data-id="element-2805" data-mp="189x7nkj8boaj">
                Requested by: {selectedRequest.merchant}
              </p>
            </div>
            <div className="space-y-4" data-id="element-2806" data-mp="yevemdlu222o">
              <div className="flex justify-between" data-id="element-2807" data-mp="yfx25hk1ch1a">
                <span className={textSec} data-id="element-2808" data-mp="udjw0s1dmh3z">Merchant IBAN</span>
                <span className={`font-bold ${textPri}`} data-id="element-2809" data-mp="2srv20swgg0ki">
                  {selectedRequest.iban.slice(0, 8)}••••••••
                  {selectedRequest.iban.slice(-4)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-2810" data-mp="1d8gjkhckita">
                <span className={textSec} data-id="element-2811" data-mp="1fup9nfz54d3">Validity</span>
                {selectedRequest.type === 'now' ? <span className="font-bold text-[#EF4444] flex items-center gap-1" data-id="element-2812" data-mp="3tjwwe7w7adrj">
                    <ClockIcon className="w-4 h-4" data-id="element-2813" data-mp="b9v26bhhzdwd" />
                    {formatTime(selectedRequest.timeLeft || 0)}
                  </span> : <span className={`font-bold ${textPri}`} data-id="element-2814" data-mp="3etpmwho6852z">
                    {selectedRequest.expiryDate}
                  </span>}
              </div>
              {selectedRequest.info && <div className="flex justify-between" data-id="element-2815" data-mp="3oj94zl427uws">
                  <span className={textSec} data-id="element-2816" data-mp="3js0wcrak0dko">Info</span>
                  <span className={`font-bold ${textPri}`} data-id="element-2817" data-mp="3jqg2z4zz70oe">
                    {selectedRequest.info}
                  </span>
                </div>}
              <div className={`h-px w-full ${borderClr} my-2`} data-id="element-2818" data-mp="2r3vc05newqld" />
              <div data-id="element-2819" data-mp="16l1aiv8cua54">
                <span className={`block text-[13px] font-bold mb-2 ${textPri}`} data-id="element-2820" data-mp="16kj4j6vw4hk0">
                  Pay From
                </span>
                <div className={`w-full rounded-2xl p-4 border ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.1)]' : 'bg-[#F9F9F9] border-[#F0E8E3]'}`} data-id="element-2821" data-mp="1bcjbvkeaicqm">
                  <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2822" data-mp="11mrfckmmr9ji">
                    {PAY_FROM_ACCOUNTS[0].label}
                  </p>
                  <p className={`text-[13px] ${textSec}`} data-id="element-2823" data-mp="1p9psb8bjjzco">
                    {PAY_FROM_ACCOUNTS[0].number}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6" data-id="element-2824" data-mp="1gd6zrx782dgm">
            <button onClick={() => {
            setRequests(prev => prev.map(r => r.id === selectedRequest.id ? {
              ...r,
              status: 'declined'
            } : r));
            navigateTo('list', -1);
          }} className={`flex-1 h-[55px] rounded-lg font-semibold text-[16px] border-2 ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2825" data-mp="1l8w4eey3mkjs">
              Decline
            </button>
            <button onClick={() => navigateTo('otp')} className="flex-1 h-[55px] rounded-lg font-semibold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2826" data-mp="1stgica7vjv10">
              Pay Now
            </button>
          </div>
        </div>
      </motion.div>;
  };
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2827" data-mp="1eu74vib1zf0q">
      {renderHeader('Enter OTP', () => navigateTo('detail', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2828" data-mp="v076jkdrw62l">
        <div className="mb-8 text-center" data-id="element-2829" data-mp="uzozdspko7fw">
          <p className={`text-[14px] ${textSec}`} data-id="element-2830" data-mp="zyid9m8ru0gl">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2831" data-mp="14qjxpvg3exhp">
          {otp.map((digit, i) => <input key={i} id={`req-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-xl text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2832" data-mp="18qu4habplrxm" data-mpi={i} />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2833" data-mp="21e6ekl0oo7a1">
          {timer > 0 ? <p className={`text-[14px] font-medium ${textSec}`} data-id="element-2834" data-mp="1zzx1d5fwdjl3">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> : <button onClick={() => setTimer(30)} className="text-[14px] font-bold text-[#F37024]" data-id="element-2835" data-mp="28zaafx04065a">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  // Payment-request receipt — canonical Send Money receipt.
  const renderSuccess = () => {
    if (!selectedRequest) return null;
    const refNumber = `RST-${selectedRequest.id.padStart(6, '0')}`;
    return <TransactionReceipt amount={selectedRequest.amount} refNumber={refNumber} toName={selectedRequest.merchant} toAccount={selectedRequest.iban} fromName={PAY_FROM_ACCOUNTS[0].holder} fromAccount={PAY_FROM_ACCOUNTS[0].number} additionalInfo={[{
      label: 'Transaction Fee',
      value: 'Rs. 0'
    }, {
      label: 'Request ID',
      value: `REQ-${selectedRequest.id.padStart(6, '0')}`
    }, ...(selectedRequest.info ? [{
      label: 'Additional Info',
      value: selectedRequest.info
    }] : [])]} onNavigateHome={onHome} data-id="element-2836" data-mp="2smzvzmhgda62" />;
  };
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2837" data-mp="2xiu1fxs7vjgg">
      {step === 'list' && renderList()}
      {step === 'detail' && renderDetail()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}